import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table, Card, Button, Typography, Tag, Space, Select, Modal, Form, Input,
  App as AntApp, Alert, Popconfirm,
} from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '@/services/api';
import { useSudoGate } from '@/hooks/useSudoGate';

const { Title } = Typography;

export default function AdminDevicesPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { require: sudoReq, modal: sudoModal, sudoActive } = useSudoGate();
  const qc = useQueryClient();
  const [vendorId, setVendorId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [purgeTarget, setPurgeTarget] = useState<string | null>(null);
  const [purgeForm] = Form.useForm();

  const vendors = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: async () => (await apiClient.get<any>('/admin/organizations')) as any,
  });

  const devices = useQuery({
    queryKey: ['admin', 'devices', vendorId],
    queryFn: async () => {
      const params: any = { limit: 200 };
      if (vendorId) params.vendorId = vendorId;
      return (await apiClient.get<any>('/admin/devices', params)) as any;
    },
  });

  const purgeMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post(`/admin/devices/${id}/purge`, {
        reason,
        confirm: 'I_UNDERSTAND_THIS_IS_IRREVERSIBLE',
      }),
    onSuccess: () => { message.success('Device purged'); setPurgeTarget(null); purgeForm.resetFields(); qc.invalidateQueries({ queryKey: ['admin', 'devices'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>{t('admin.devicesTitle')}</Title>
        {sudoActive && <Tag color="orange">SUDO</Tag>}
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="Search UUID / name" prefix={<SearchOutlined />} allowClear
            style={{ width: 300 }} value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select placeholder="Filter by vendor" allowClear style={{ width: 240 }} value={vendorId}
            onChange={setVendorId}
            options={vendors.data?.vendors?.map((v: any) => ({ label: v.name, value: v.idx })) ?? []} />
        </Space>
      </Card>

      <Card>
        <Table size="small" rowKey="idx" loading={devices.isLoading}
          dataSource={(devices.data?.devices ?? []).filter((d: any) => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (d.idx?.toLowerCase().includes(s)) || (d.name?.toLowerCase().includes(s));
          })} pagination={{ pageSize: 50 }}
          columns={[
            { title: 'UUID', dataIndex: 'idx', key: 'idx', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code> },
            { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string | null) => v || <Tag>—</Tag> },
            { title: 'Enabled', dataIndex: 'enabled', key: 'en', width: 90, render: (v: boolean) => v ? <Tag color="green">YES</Tag> : <Tag>NO</Tag> },
            { title: 'Purged', dataIndex: 'purged', key: 'pu', width: 90, render: (v: boolean) => v ? <Tag color="red">PURGED</Tag> : '—' },
            { title: 'Actions', key: 'act', width: 120, render: (_, r: any) => (
              !r.purged && (
                <Button size="small" danger icon={<DeleteOutlined />}
                  onClick={() => sudoReq(() => setPurgeTarget(r.idx))}>
                  {t('admin.purge')}
                </Button>
              )
            )},
          ]}
        />
      </Card>

      <Modal title={t('admin.purgeConfirm')} open={!!purgeTarget}
        onCancel={() => { setPurgeTarget(null); purgeForm.resetFields(); }}
        onOk={() => purgeForm.submit()} confirmLoading={purgeMut.isPending}
        okText="Purge" okButtonProps={{ danger: true }}>
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
          message="This action is irreversible"
          description="All identifying data (name, type, BT_MAC, public key) will be permanently erased. The UUID itself is retained for referential integrity." />
        <Form form={purgeForm} layout="vertical" onFinish={(v) => {
          if (purgeTarget) purgeMut.mutate({ id: purgeTarget, reason: v.reason });
        }}>
          <Form.Item name="reason" label="Reason (min 10 chars)" rules={[{ required: true, min: 10 }]}>
            <Input.TextArea rows={3} placeholder="e.g. GDPR deletion request from customer" />
          </Form.Item>
        </Form>
      </Modal>

      {sudoModal}
    </>
  );
}
